import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Download } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { formatApiError } from "../../lib/auth";

const Progress = ({ job }) => {
  if (!job || job.state === "idle") return null;
  const pct = job.total ? Math.round((job.done / job.total) * 100) : 0;
  const tone = job.state === "error" ? "border-red-700 text-red-700" : job.state === "done" ? "border-ink bg-blush" : "border-ink";
  return (
    <div className={`border-2 p-5 mt-8 ${tone}`} data-testid="import-status">
      <p className="kicker">{job.state === "running" ? `Working… ${job.done ?? 0} / ${job.total ?? 0}` : job.state}</p>
      {job.state === "running" && (
        <div className="h-2 bg-cream border border-ink mt-3"><div className="h-full bg-ink transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
      )}
      <p className="mt-3 text-base" data-testid="import-message">{job.message}</p>
    </div>
  );
};

export default function AdminImport() {
  const qc = useQueryClient();
  const [source, setSource] = useState("");
  const [replace, setReplace] = useState(true);
  const [job, setJob] = useState(null);

  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await api.get("/admin/import/status");
        if (!alive) return;
        setJob(data);
        if (data?.state === "done") qc.invalidateQueries();
      } catch {
        /* ignore */
      }
    };
    poll();
    const t = setInterval(poll, 2000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [qc]);

  const start = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/import", { source, replace });
      setJob({ state: "running", done: 0, total: 0, message: "Reading articles" });
      toast.success("Import started");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const running = job?.state === "running";
  return (
    <main className="pt-28 pb-24 max-w-3xl mx-auto px-5 sm:px-10" data-testid="admin-import-page">
      <Link to="/admin" className="inline-flex items-center gap-2 kicker hover:text-rose" data-testid="import-back-link"><ArrowLeft className="h-4 w-4" /> All articles</Link>
      <h1 className="display text-5xl sm:text-7xl mt-6">Import</h1>
      <p className="font-serif italic text-xl mt-3 text-ink/80">
        Copy every live article, its photos, the home banner and the downloadable magazine PDF from another copy of this site (for example the preview) into this one.
      </p>
      <form onSubmit={start} className="mt-10 space-y-5">
        <label className="block">
          <span className="kicker">Copy from (site address)</span>
          <input value={source} onChange={(e) => setSource(e.target.value)} required placeholder="https://your-preview-address" className="admin-input mt-2" data-testid="import-source-input" />
        </label>
        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} className="accent-[#FF3D8B] h-5 w-5 mt-0.5" data-testid="import-replace-checkbox" />
          <span className="text-base">Delete the articles currently on this site first (recommended, removes the demo articles)</span>
        </label>
        <p className="text-sm text-ink/70">Drafts and scheduled articles are not copied, only articles that are live on the source site.</p>
        <button disabled={running} className="inline-flex items-center gap-2 bg-hot text-ink border-2 border-ink kicker px-6 py-4 hover:bg-ink hover:text-cream transition-colors disabled:opacity-60" data-testid="import-start-btn">
          <Download className="h-4 w-4" /> {running ? "Importing…" : "Start import"}
        </button>
      </form>
      <Progress job={job} />
    </main>
  );
}
